import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import { PenTool, Wrench, CheckCircle, ArrowRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

interface Step {
  key: string
  number: string
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
}

export function ProcessSteps() {
  const { t } = useTranslation()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  })

  const lineProgress = useTransform(scrollYProgress, [0.2, 0.6], [0, 1])

  const steps: Step[] = [
    {
      key: 'design',
      number: '01',
      title: t('nav.design'),
      description: t('process.designDesc'),
      icon: PenTool,
    },
    {
      key: 'manufacturing',
      number: '02',
      title: t('nav.manufacturing'),
      description: t('process.manufacturingDesc'),
      icon: Wrench,
    },
    {
      key: 'finalStage',
      number: '03',
      title: t('nav.finalStage'),
      description: t('process.finalStageDesc'),
      icon: CheckCircle,
    },
  ]

  return (
    <section ref={ref} className="section relative overflow-hidden bg-[var(--color-void)]">
      {/* Background Grid Pattern */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(to right, var(--color-graphite) 1px, transparent 1px),
            linear-gradient(to bottom, var(--color-graphite) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
        }}
      />

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-ink)] via-transparent to-[var(--color-ink)]" />

      {/* Accent Glow */}
      <div className="absolute right-0 top-1/2 h-[800px] w-[600px] -translate-y-1/2 translate-x-1/2 rounded-full bg-[var(--accent-primary)]/5 blur-[200px]" />

      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-20 text-center"
        >
          <div className="section-label justify-center">
            <span className="label">{t('nav.processes')}</span>
          </div>
          <h2 className="headline-xl mt-6">
            {t('homepage.process.title')}
          </h2>
          <p className="body-lg mx-auto mt-6 max-w-2xl">
            {t('homepage.process.subtitle')}
          </p>
        </motion.div>

        {/* Steps Container */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="absolute left-1/2 top-24 hidden h-0.5 w-[60%] -translate-x-1/2 lg:block">
            <div className="absolute inset-0 bg-[var(--color-graphite)]" />
            <motion.div
              className="absolute inset-0 origin-left bg-gradient-to-r from-[var(--accent-primary)] via-[var(--color-electric)] to-[var(--accent-primary)]"
              style={{ scaleX: lineProgress }}
            />
          </div>

          {/* Steps Grid */}
          <div className="grid gap-8 lg:grid-cols-3 lg:gap-12">
            {steps.map((step, idx) => {
              const Icon = step.icon

              return (
                <motion.div
                  key={step.key}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{
                    duration: 0.7,
                    delay: idx * 0.15,
                    ease: [0.16, 1, 0.3, 1]
                  }}
                  className="group relative"
                >
                  {/* Card */}
                  <div className="relative">
                    {/* Glow Effect */}
                    <div className="absolute -inset-2 rounded-2xl bg-[var(--accent-primary)]/0 opacity-0 blur-xl transition-all duration-500 group-hover:bg-[var(--accent-primary)]/10 group-hover:opacity-100" />

                    {/* Card Content */}
                    <div className="relative rounded-xl border border-[var(--color-graphite)] bg-[var(--color-charcoal)]/50 p-8 backdrop-blur-sm transition-all duration-500 group-hover:border-[var(--color-steel)] group-hover:bg-[var(--color-charcoal)]">
                      {/* Step Number Badge */}
                      <div className="absolute -top-5 left-8 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-ink)] text-lg font-bold text-[var(--accent-primary)] shadow-lg transition-colors duration-300 group-hover:bg-[var(--accent-primary)] group-hover:text-white">
                        {step.number}
                      </div>

                      {/* Icon */}
                      <div className="mb-6 mt-4">
                        <div className="inline-flex h-16 w-16 items-center justify-center rounded-xl bg-[var(--color-graphite)] text-[var(--accent-primary)] transition-all duration-300 group-hover:bg-[var(--accent-primary)] group-hover:text-white group-hover:shadow-lg group-hover:shadow-[var(--accent-primary)]/20">
                          <Icon className="h-7 w-7" />
                        </div>
                      </div>

                      {/* Content */}
                      <h3 className="headline-md mb-3 transition-colors duration-300 group-hover:text-[var(--accent-primary)]">
                        {step.title}
                      </h3>
                      <p className="body-sm">
                        {step.description}
                      </p>

                      {/* Bottom Accent Line */}
                      <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-[var(--accent-primary)] transition-all duration-500 group-hover:w-full" />
                    </div>
                  </div>

                  {/* Connector Dot */}
                  {idx < steps.length - 1 && (
                    <div className="absolute -right-6 top-24 hidden h-3 w-3 -translate-y-1/2 rounded-full border-2 border-[var(--color-graphite)] bg-[var(--color-ink)] lg:block" />
                  )}
                </motion.div>
              )
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 text-center"
        >
          <LocalizedLink
            to="processes"
            className="btn btn-primary group"
          >
            {t('buttons.discoverMore')}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </LocalizedLink>
        </motion.div>
      </div>
    </section>
  )
}
